import * as React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import { ReadOnlyDataGrid, ReadOnlyField, RowAction } from '../components/ReadOnlyDataGrid';
import { PageLayout } from '../components/layout';
import { ActionButton } from '../components/common';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import {
  useIncidenceEnea,
  useCreateIncidenceEnea,
  useUpdateIncidenceEnea,
  useDeleteIncidenceEnea,
  IncidenceEneaReadDTO,
  IncidenceEneaCreateDTO,
} from '../hooks/useIncidenceEnea';
import { IncidenceEneaPercentagesDialog } from '../components/IncidenceEneaPercentagesDialog';
import toast from 'react-hot-toast';
import { IIncidenceEneaCreateDTO } from '../generated/api-client';

export const IncidenceEneaPage: React.FC = () => {
  const { data: incidences = [], isLoading, error } = useIncidenceEnea();
  const deleteMutation = useDeleteIncidenceEnea();
  const createMutation = useCreateIncidenceEnea();
  const updateMutation = useUpdateIncidenceEnea();

  const [deleteState, setDeleteState] = React.useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const [editDialogState, setEditDialogState] = React.useState<{
    open: boolean;
    incidence: IncidenceEneaReadDTO | null;
  }>({
    open: false,
    incidence: null,
  });

  const [percentagesDialogState, setPercentagesDialogState] = React.useState<{
    open: boolean;
    incidenceId: string | null;
  }>({
    open: false,
    incidenceId: null,
  });

  const [formData, setFormData] = React.useState<IIncidenceEneaCreateDTO>({
    code: '',
    unit_of_measurement: '',
    codice_ateco: [],
    minimum_percentage_consideration: undefined,
  });

  const fields: ReadOnlyField[] = [
    {
      field: 'code',
      headerName: 'Codice',
      width: 200,
    },
    {
      field: 'unit_of_measurement',
      headerName: 'Unità di misura',
      width: 150,
    },
    {
      field: 'minimum_percentage_consideration',
      headerName: 'Min consideration (%)',
      width: 200,
      type: 'number',
    },
    {
      field: 'codice_ateco',
      headerName: 'Codici ATECO',
      width: 300,
      valueFormatter: value => {
        if (!value) return '';
        if (Array.isArray(value)) {
          return value.length > 0 ? value.join(', ') : '';
        }
        return String(value);
      },
    },
  ];

  const actions: RowAction[] = [
    {
      label: 'Modifica',
      icon: <EditIcon />,
      onClick: (id, row) => {
        setFormData({
          code: row.code || '',
          unit_of_measurement: row.unit_of_measurement || '',
          codice_ateco: Array.isArray(row.codice_ateco) ? row.codice_ateco : [],
          minimum_percentage_consideration:
            typeof row.minimum_percentage_consideration === 'number'
              ? row.minimum_percentage_consideration
              : undefined,
        });
        setEditDialogState({ open: true, incidence: row as IncidenceEneaReadDTO });
      },
    },
    {
      label: 'Percentuali',
      icon: <SettingsIcon />,
      onClick: id => {
        setPercentagesDialogState({ open: true, incidenceId: String(id) });
      },
    },
    {
      label: 'Elimina',
      icon: <DeleteIcon />,
      onClick: id => {
        setDeleteState({ open: true, id: String(id) });
      },
    },
  ];

  const handleAdd = () => {
    setFormData({
      code: '',
      unit_of_measurement: '',
      codice_ateco: [],
      minimum_percentage_consideration: undefined,
    });
    setEditDialogState({ open: true, incidence: null });
  };

  const handleSave = () => {
    // Prepare data: if codice_ateco is empty array, send null instead
    const dataToSave: IIncidenceEneaCreateDTO = {
      code: formData.code,
      unit_of_measurement: formData.unit_of_measurement || undefined,
      codice_ateco:
        formData.codice_ateco && formData.codice_ateco.length > 0
          ? formData.codice_ateco
          : undefined,
      minimum_percentage_consideration:
        formData.minimum_percentage_consideration !== undefined &&
        formData.minimum_percentage_consideration !== null &&
        !Number.isNaN(formData.minimum_percentage_consideration)
          ? Number(formData.minimum_percentage_consideration)
          : undefined,
    };

    if (editDialogState.incidence) {
      // Update
      updateMutation.mutate(
        {
          id: editDialogState.incidence.id!,
          data: dataToSave as IncidenceEneaCreateDTO,
        },
        {
          onSuccess: () => {
            toast.success('Incidenza aggiornata con successo');
            setEditDialogState({ open: false, incidence: null });
          },
          onError: (error: any) => {
            toast.error(error?.message || "Errore nell'aggiornamento");
          },
        }
      );
    } else {
      // Create
      createMutation.mutate(dataToSave, {
        onSuccess: () => {
          toast.success('Incidenza creata con successo');
          setEditDialogState({ open: false, incidence: null });
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Errore nella creazione');
        },
      });
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Errore nel caricamento delle incidenze ENEA</Typography>
      </Box>
    );
  }

  return (
    <>
      <PageLayout
        header={
          <>
            <AssessmentIcon fontSize="large" />
            <Typography variant="h5" sx={{ marginLeft: '1rem' }}>
              Incidenze ENEA
            </Typography>
          </>
        }
        actions={
          <ActionButton icon={<AddIcon />} tooltip="Aggiungi nuova incidenza" onClick={handleAdd} />
        }
      >
        <Box sx={{ p: 2 }}>
          <ReadOnlyDataGrid
            fields={fields}
            data={incidences}
            actions={actions}
            loading={isLoading}
            getId={row => row.id || ''}
          />
        </Box>
      </PageLayout>

      {/* Edit/Create Dialog */}
      <Dialog
        open={editDialogState.open}
        onClose={() => setEditDialogState({ open: false, incidence: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editDialogState.incidence
            ? 'Modifica Incidenza vettore energetico'
            : 'Nuova Incidenza vettore energetico'}
          <IconButton
            aria-label="close"
            onClick={() => setEditDialogState({ open: false, incidence: null })}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Codice"
              value={formData.code}
              onChange={e => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Unità di misura"
              value={formData.unit_of_measurement}
              onChange={e => setFormData({ ...formData, unit_of_measurement: e.target.value })}
              fullWidth
            />
            <TextField
              label="Codici ATECO (separati da virgola)"
              value={Array.isArray(formData.codice_ateco) ? formData.codice_ateco.join(', ') : ''}
              onChange={e => {
                const codes = e.target.value
                  .split(',')
                  .map(c => c.trim())
                  .filter(c => c);
                setFormData({ ...formData, codice_ateco: codes });
              }}
              fullWidth
            />
            <TextField
              label="Minima percentuale di considerazione (%)"
              type="number"
              value={
                formData.minimum_percentage_consideration !== undefined &&
                formData.minimum_percentage_consideration !== null
                  ? formData.minimum_percentage_consideration
                  : ''
              }
              onChange={e =>
                setFormData({
                  ...formData,
                  minimum_percentage_consideration:
                    e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogState({ open: false, incidence: null })}>
            Annulla
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.code}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Percentages Dialog */}
      {percentagesDialogState.incidenceId && (
        <IncidenceEneaPercentagesDialog
          open={percentagesDialogState.open}
          incidenceEneaId={percentagesDialogState.incidenceId}
          onClose={() => setPercentagesDialogState({ open: false, incidenceId: null })}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteState.open}
        title="Conferma eliminazione"
        content="Sei sicuro di voler eliminare questa incidenza vettore energetico?"
        confirmText="Elimina"
        cancelText="Annulla"
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteState({ open: false, id: null })}
        onConfirm={() => {
          if (deleteState.id) {
            deleteMutation.mutate(deleteState.id, {
              onSettled: () => setDeleteState({ open: false, id: null }),
            });
          }
        }}
      />
    </>
  );
};
